import { useEffect, useRef } from "react";
import { useGuestMode } from "../../js/hooks/useGuestMode";

/**
 * Hard lock for interactive surfaces in guest / view-only mode.
 * Overlay catches clicks; controls are disabled/readOnly so typing cannot modify data.
 * Navigation outside this shell stays enabled.
 */
export default function GuestLockShell({ children, className = "" }) {
  const { isViewOnly, openGuestLock } = useGuestMode();
  const surfaceRef = useRef(null);

  useEffect(() => {
    if (!isViewOnly || !surfaceRef.current) return undefined;

    const root = surfaceRef.current;
    const controls = root.querySelectorAll("input, textarea, select, button");

    controls.forEach((el) => {
      if (el.closest(".wizard-actions, .journey-mini-nav, .journey-sidebar__toggle, .journey-sidebar__chrome")) {
        return;
      }
      if (el.tagName === "TEXTAREA" || (el.tagName === "INPUT" && !["checkbox", "radio", "file", "button", "submit", "reset"].includes(el.type))) {
        el.setAttribute("readonly", "true");
        el.setAttribute("data-guest-readonly", "1");
      } else {
        el.setAttribute("disabled", "true");
        el.setAttribute("data-guest-disabled", "1");
      }
    });

    return () => {
      root.querySelectorAll("[data-guest-readonly]").forEach((el) => {
        el.removeAttribute("readonly");
        el.removeAttribute("data-guest-readonly");
      });
      root.querySelectorAll("[data-guest-disabled]").forEach((el) => {
        el.removeAttribute("disabled");
        el.removeAttribute("data-guest-disabled");
      });
    };
  }, [isViewOnly, children]);

  if (!isViewOnly) {
    return children;
  }

  function onOverlayInteract(event) {
    event.preventDefault();
    event.stopPropagation();
    openGuestLock();
  }

  return (
    <div className={`guest-lock ${className}`.trim()} data-guest-lock="true">
      <span className="guest-lock__badge" aria-hidden="true" title="Preview Only">
        Preview Only
      </span>
      <div className="guest-lock__surface" ref={surfaceRef} aria-disabled="true">
        {children}
      </div>
      <button
        type="button"
        className="guest-lock__overlay"
        aria-label="Preview only. Learn how to continue with your agent or broker."
        onClick={onOverlayInteract}
        onPointerDown={onOverlayInteract}
      />
    </div>
  );
}
